import React from 'react';
import { Box, Container, Typography, Paper } from '@mui/material';
import ContactForm from '../Contact/ContactForm';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { SEOHead, LocalBusinessSchema, BreadcrumbSchema } from '../SEO';

const ContactPage: React.FC = () => {
  const breadcrumbs = BreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Contact Us", url: "/contact" }
  ]);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [LocalBusinessSchema, breadcrumbs]
  };

  return (
    <>
      <SEOHead
        title="Contact Maa Saryu Agency - Get Quote for Tiles & Sanitary Ware in Ayodhya | Call Now"
        description="Contact Maa Saryu Agency for premium tiles, granite & sanitary ware in Ayodhya. ✅ Free quotes ✅ Expert consultation ✅ Fast response. Call +91 7897239423 or visit our showroom."
        keywords={[
          "contact maa saryu agency",
          "tiles quote ayodhya",
          "sanitary ware consultation ayodhya", 
          "granite price ayodhya",
          "bathroom tiles consultation",
          "tiles showroom ayodhya contact",
          "home renovation consultation ayodhya",
          "tiles dealer contact ayodhya",
          "bathroom fittings quote ayodhya",
          "construction material supplier contact",
          "tiles installation service ayodhya",
          "free tiles consultation",
          "ayodhya tiles showroom address",
          "maa saryu agency phone number"
        ]}
        canonicalUrl="/contact"
        ogTitle="Contact Maa Saryu Agency - Get Free Quote for Tiles & Sanitary Ware"
        ogDescription="📞 Contact us for premium tiles, granite & sanitary ware in Ayodhya. Get free quotes & expert consultation. Call +91 7897239423 or visit our showroom!"
        structuredData={structuredData}
      />
      <Box sx={{ py: 8, backgroundColor: '#f8f9fa', minHeight: '80vh' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 'bold', 
            color: '#1a237e',
            mb: 2,
            fontSize: { xs: '2rem', md: '3rem' }
          }}>
            Contact Us
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6
          }}>
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          gap: 4 
        }}>
          {/* Contact Form */}
          <Box sx={{ flex: { md: '0 0 58%' } }}>
            <Paper elevation={3} sx={{ 
              p: 4, 
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
            }}>
              <Typography variant="h5" component="h2" sx={{ 
                fontWeight: 'bold', 
                color: '#1a237e',
                mb: 3
              }}>
                Send us a Message
              </Typography>
              <ContactForm />
            </Paper>
          </Box>

          {/* Contact Information */}
          <Box sx={{ flex: { md: '0 0 42%' } }}>
            <Box sx={{ pl: { md: 2 } }}>
              <Typography variant="h5" component="h2" sx={{ 
                fontWeight: 'bold', 
                color: '#1a237e',
                mb: 3
              }}>
                Get in Touch
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography variant="body1" sx={{ 
                  color: '#666',
                  lineHeight: 1.7,
                  mb: 3
                }}>
                  Have questions about our services? Need a custom quote for your project? 
                  We're here to help! Reach out to us through any of the following channels.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Email */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    backgroundColor: '#ffca28', 
                    borderRadius: '50%', 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <EmailIcon sx={{ color: '#1a237e', fontSize: '1.5rem' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      maasaryuagency@gmail.com
                    </Typography>
                  </Box>
                </Box>

                {/* Phone */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    backgroundColor: '#ffca28', 
                    borderRadius: '50%', 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <PhoneIcon sx={{ color: '#1a237e', fontSize: '1.5rem' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                      Phone
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      +91 7897239423
                    </Typography>
                  </Box>
                </Box>

                {/* Address */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    backgroundColor: '#ffca28', 
                    borderRadius: '50%', 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <LocationOnIcon sx={{ color: '#1a237e', fontSize: '1.5rem' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                      Address
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Ashapur Rd, Darshan Nagar<br />
                      Ayodhya, Ashapur, Uttar Pradesh 224135
                    </Typography>
                  </Box>
                </Box>

                {/* Business Hours */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    backgroundColor: '#ffca28', 
                    borderRadius: '50%', 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <AccessTimeIcon sx={{ color: '#1a237e', fontSize: '1.5rem' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                      Business Hours
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Fri - Wed: 10:00 AM - 8:00 PM<br />
                      Thursday - Showroom will be closed
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* CTA Section */}
              <Box sx={{ 
                mt: 4, 
                p: 3, 
                backgroundColor: '#e8eaf6', 
                borderRadius: '8px',
                border: '1px solid #c5cae9'
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: '#1a237e',
                  mb: 1
                }}>
                  Need Quick Support?
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#666',
                  mb: 2
                }}>
                  For urgent inquiries, feel free to call us directly or send a WhatsApp message.
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#1a237e',
                  fontWeight: 'bold'
                }}>
                  Response time: Usually within 2-4 hours
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Google Maps Section */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h4" component="h2" sx={{ 
            fontWeight: 'bold', 
            color: '#1a237e',
            mb: 3,
            textAlign: 'center'
          }}>
            Find Us on Map
          </Typography>
          <Paper elevation={3} sx={{ 
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
          }}>
            <Box sx={{ 
              position: 'relative',
              height: { xs: '300px', md: '450px' },
              width: '100%'
            }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3586.789!2d82.182826!3d26.774348!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399a0774bf3a4c31%3A0x2046914c4933a21a!2sMaa%20Saryu%20Agency%2C%20Ashapur%20Rd%2C%20Darshan%20Nagar%2C%20Ayodhya%2C%20Ashapur%2C%20Uttar%20Pradesh%20224135%2C%20India!5e0!3m2!1sen!2sin!4v1706789123456!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Maa Saryu Agency Location Map"
              />
            </Box>
          </Paper>
        </Box>
        </Container>
      </Box>
    </>
  );
};

export default ContactPage;
